import React, { useMemo, useRef, useState } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { FunnelIcon } from '@heroicons/react/24/outline'
import {
  getStatusBadgeColor,
  formatChallengeStatus,
  getFeedbackStatusBadgeColor,
  formatFeedbackGrade,
  statusFilterOptions,
  feedbackStatusFilterOptions,
  getFeedbackStatusFilterBadgeColor,
  type Column
} from './adminChallenges.utils'
import { formatWorkTime } from '@/utils/time.utils'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { useTar } from '@/modules'
import {
  AsyncMultiPick,
  Button,
  CopyableId,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  type MultiPickOption,
} from '@duck/ui'
import { toast } from 'sonner'
import { apis } from '../../modules/apiClients'
import { useLoadMoreOnScroll } from '@/hooks/useLoadMoreOnScroll'
import { renderSortIcon } from '@/hooks/TableSettings/generalTable.utils'
import type { EnumFilterOption } from '@/hooks/TableSettings/generalTable.utils'
import {
  TextFilterPopover,
  EnumFilterDropdown,
  ColumnVisibilityDropdown,
} from '@/hooks/TableSettings/tableSettings.components'
import { FilterSetManager } from '@/hooks/TableSettings/FilterSetManager'
import type { SortDirection, TableSortState } from '@/hooks/TableSettings/useTableSettings'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { StandardLoader } from '@/components/StandardLoader'
import {
  EllipsisVerticalIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  BriefcaseIcon,
  TrashIcon,
  ArrowUpOnSquareIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

interface ChallengesTableFilterApi {
  getColumnFilter: (columnKey: string) => string
  setColumnFilter: (columnKey: string, value: string) => void
  getColumnFilterArray: (columnKey: string) => string[]
  setColumnFilterArray: (columnKey: string, value: string[]) => void
  toggleColumnFilterArrayItem: (columnKey: string, item: string) => void
  clearColumnFilter: (columnKey: string) => void
}

/** Map of async-search filter keys to their fetch functions. */
interface AsyncSearchFetchers {
  [filterKey: string]: (query?: string) => Promise<MultiPickOption[]>
}

interface ChallengesTableProps {
  challenges: ChallengeService.ChallengeInfo[]
  totalChallenges: number
  visibleColumns: Column[]
  sortField: keyof ChallengeService.Challenge
  sortDirection: 'asc' | 'desc'
  onSort: (field: keyof ChallengeService.Challenge) => void
  onChallengeClick?: (challengeInfo: ChallengeService.ChallengeInfo) => void
  /** Called after a challenge is successfully deleted from the row menu (e.g. to refetch the list). */
  onChallengeDeleted?: () => void
  onViewFeedback?: (challengeInfo: ChallengeService.ChallengeInfo) => void
  onViewEmployerFeedback?: (challengeInfo: ChallengeService.ChallengeInfo) => void
  onViewSolution?: (challengeInfo: ChallengeService.ChallengeInfo) => void
  loadingMode?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  isPageLoading?: boolean
  filterApi?: ChallengesTableFilterApi
  /** Fetch functions for async-search filter columns, keyed by filterKey. */
  asyncSearchFetchers?: AsyncSearchFetchers
  hasNoFilterResults?: boolean
  onClearFilters?: () => void
  /** When provided, Clear all + Column visibility are shown in the table header */
  tableSettings?: {
    hasActiveSettings: () => boolean
    clearAllSettings: () => void
    isColumnVisible: (key: string) => boolean
    toggleColumnVisibility: (key: string) => void
    availableColumns: Array<{ key: string; label: string }>
    /** Filter set manager props */
    tableId: string
    sortColumn: string | null
    sortDirection: SortDirection
    filters: Record<string, string | string[]>
    visibleColumns: Record<string, boolean>
    defaultVisibleColumns: Record<string, boolean>
    filterLabels: Record<string, Record<string, string>>
    setSort: (columnKey: string | null, direction: SortDirection) => void
    clearColumnFilter: (columnKey: string) => void
    applyFilterSet: (sort: TableSortState, filters: Record<string, string | string[]>, visibleColumns?: Record<string, boolean>, filterLabels?: Record<string, Record<string, string>>) => void
  }
}

export const ChallengesTable: React.FC<ChallengesTableProps> = ({
  challenges,
  totalChallenges,
  visibleColumns,
  sortField,
  sortDirection,
  onSort,
  onChallengeClick,
  onChallengeDeleted,
  onViewFeedback,
  onViewEmployerFeedback,
  onViewSolution,
  loadingMode = false,
  hasMore = false,
  onLoadMore,
  isPageLoading = false,
  filterApi,
  asyncSearchFetchers,
  hasNoFilterResults = false,
  onClearFilters,
  tableSettings,
}) => {
  const { t } = useTar()

  const getEnumOptionsForColumn = useMemo((): ((filterKey: string) => EnumFilterOption[] | undefined) => {
    return (filterKey: string) => {
      if (filterKey === 'status') {
        return statusFilterOptions.map((o) => ({
          value: o.value,
          label: o.label,
          component: (
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(o.value as ChallengeService.ChallengeStatusEnum)}`}
            >
              {t(o.label)}
            </span>
          ),
        }))
      }
      if (filterKey === 'feedbackStatus') {
        return feedbackStatusFilterOptions.map((o) => ({
          value: o.value,
          label: o.label,
          component: (
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getFeedbackStatusFilterBadgeColor(o.value)}`}
            >
              {t(o.label)}
            </span>
          ),
        }))
      }
      return undefined
    }
  }, [t])

  const [feedbackReleaseState, setFeedbackReleaseState] = useState<Record<string, boolean | undefined>>({})
  const [feedbackReleasePending, setFeedbackReleasePending] = useState<Record<string, true>>({})
  const [pdfDownloadingChallengeId, setPdfDownloadingChallengeId] = useState<string | null>(null)
  const [openDropdownChallengeId, setOpenDropdownChallengeId] = useState<string | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [deleteConfirmReady, setDeleteConfirmReady] = useState(false)
  const [resubmitPending, setResubmitPending] = useState<Record<string, true>>({})
  const [resubmitConfirmId, setResubmitConfirmId] = useState<string | null>(null)
  const deleteConfirmTimeoutRef = useRef<number | null>(null)

  const clearDeleteConfirmTimeout = () => {
    if (deleteConfirmTimeoutRef.current != null) {
      window.clearTimeout(deleteConfirmTimeoutRef.current)
      deleteConfirmTimeoutRef.current = null
    }
  }

  const startDeleteConfirmTimeout = (challengeId: string) => {
    clearDeleteConfirmTimeout()
    setDeleteConfirmReady(false)
    deleteConfirmTimeoutRef.current = window.setTimeout(() => {
      setDeleteConfirmReady(true)
    }, 2000)
  }

  const handleDeleteMenuClick = async (
    event: React.MouseEvent<HTMLDivElement>,
    challengeInfo: ChallengeService.ChallengeInfo
  ) => {
    event.stopPropagation()
    event.preventDefault()
    const id = challengeInfo.challenge.id

    if (deleteConfirmId === id && deleteConfirmReady) {
      clearDeleteConfirmTimeout()
      try {
        await apis.challengeService.deleteChallenge(id)
        onChallengeDeleted?.()
      } catch (error) {
        console.error('Failed to delete challenge from table menu', { id, error })
      } finally {
        setDeleteConfirmId(null)
        setDeleteConfirmReady(false)
      }
      return
    }

    setDeleteConfirmId(id)
    startDeleteConfirmTimeout(id)
  }

  const { sentinelRef: loadMoreSentinelRef, sentinelIndex: loadMoreSentinelIndex } = useLoadMoreOnScroll<HTMLTableRowElement>({
    hasMore,
    onLoadMore,
    isLoading: isPageLoading,
    disabled: loadingMode,
    itemCount: challenges.length,
    offsetFromEnd: 10,
  })

  const handleDownloadFeedbackPdf = async (challengeId: string) => {
    if (pdfDownloadingChallengeId) return
    setPdfDownloadingChallengeId(challengeId)
    try {
      const response = await apis.challengeService.getFeedbackPdf(challengeId, {
        responseType: 'blob',
      })
      const blob = response.data as Blob
      const contentDisposition = response.headers['content-disposition']
      let fileName: string | undefined
      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
        if (filenameStarMatch?.[1]) {
          try {
            fileName = decodeURIComponent(filenameStarMatch[1])
          } catch {
            // fall back to basic filename
          }
        }
        if (!fileName) {
          const fileNameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/)
          fileName = fileNameMatch?.[1]
        }
      }
      fileName = fileName ?? `feedback_${challengeId}.pdf`
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch {
      toast.error(t('challenges.failedToDownloadPdf'))
    } finally {
      setPdfDownloadingChallengeId(null)
      setOpenDropdownChallengeId(null)
    }
  }

  const handleToggleFeedbackRelease = async (challengeInfo: ChallengeService.ChallengeInfo, release: boolean) => {
    const challengeId = challengeInfo.challenge.id
    if (feedbackReleasePending[challengeId]) return

    setFeedbackReleasePending(prev => ({ ...prev, [challengeId]: true }))
    setFeedbackReleaseState(prev => ({ ...prev, [challengeId]: release }))

    try {
      await apis.challengeService.releaseFeedback({ challengeId, release })
    } catch (error) {
      console.error('Failed to update feedback release override', { challengeId, release, error })
      setFeedbackReleaseState(prev => ({ ...prev, [challengeId]: !release }))
    } finally {
      setFeedbackReleasePending(prev => {
        const next = { ...prev }
        delete next[challengeId]
        return next
      })
    }
  }

  const handleResubmitChallenge = async (
    event: React.MouseEvent<HTMLDivElement>,
    challengeInfo: ChallengeService.ChallengeInfo
  ) => {
    event.stopPropagation()
    event.preventDefault()
    const challengeId = challengeInfo.challenge.id
    if (resubmitPending[challengeId]) return

    if (resubmitConfirmId !== challengeId) {
      setResubmitConfirmId(challengeId)
      return
    }

    setResubmitConfirmId(null)
    setResubmitPending(prev => ({ ...prev, [challengeId]: true }))
    try {
      await apis.challengeService.resubmitChallenge({ challengeId })
      toast.success(t('challenges.resubmitSuccess'))
      onChallengeDeleted?.() // refetch list
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (error as Error)?.message
        ?? 'Unknown error'
      console.error('Failed to resubmit challenge', { challengeId, error })
      toast.error(`${t('challenges.resubmitFailed')}: ${message}`)
    } finally {
      setResubmitPending(prev => {
        const next = { ...prev }
        delete next[challengeId]
        return next
      })
    }
  }

  const cellRenderers: Partial<Record<keyof ChallengeService.Challenge, (challengeInfo: ChallengeService.ChallengeInfo) => React.ReactNode>> = {
    id: (challengeInfo) => {
      const id = challengeInfo.challenge.id
      return (
        <CopyableId
          value={id}
          className="bg-muted/20 hover:bg-muted/40 hover:text-primary rounded px-1.5 py-0.5"
        />
      )
    },
    slug: (challengeInfo) => (
      <span className="text-xs font-mono text-muted-foreground">
        {challengeInfo.challenge.slug}
      </span>
    ),
    userId: (challengeInfo) => (
      <span className="text-sm text-foreground">
        {challengeInfo.user?.fullName || challengeInfo.challenge.userId}
      </span>
    ),
    taskId: (challengeInfo) => (
      <span className="text-sm text-foreground">
        {challengeInfo.task?.name || challengeInfo.challenge.taskId}
      </span>
    ),
    status: (challengeInfo) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(challengeInfo.challenge.status)}`}>
        {t(formatChallengeStatus(challengeInfo.challenge.status))}
      </span>
    ),
    feedbackStatus: (challengeInfo) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getFeedbackStatusBadgeColor(challengeInfo.challenge.feedbackStatus)}`}>
        {challengeInfo.challenge.feedbackStatus ? challengeInfo.challenge.feedbackStatus.charAt(0).toUpperCase() + challengeInfo.challenge.feedbackStatus.slice(1) : 'Unprocessed'}
      </span>
    ),
    workTime: (challengeInfo) => (
      <span className="text-sm text-foreground">
        {formatWorkTime(challengeInfo.challenge.workTime, t)}
      </span>
    ),
    feedbackGrade: (challengeInfo) => (
      <span className="text-sm text-foreground">
        {formatFeedbackGrade(challengeInfo.challenge.feedbackGrade)}
      </span>
    ),
    startTime: (challengeInfo) => (
      <RelativeDateTime
        isoDateString={challengeInfo.challenge.startTime}
        className="text-sm text-muted-foreground"
      />
    ),
    endTime: (challengeInfo) => (
      challengeInfo.challenge.endTime ? (
        <RelativeDateTime
          isoDateString={challengeInfo.challenge.endTime}
          className="text-sm text-muted-foreground"
        />
      ) : (
        <span className="text-sm text-muted-foreground">
          {t('common.notApplicable')}
        </span>
      )
    ),
    lastWorkTimeUpdate: (challengeInfo) => (
      challengeInfo.challenge.lastWorkTimeUpdate ? (
        <RelativeDateTime
          isoDateString={challengeInfo.challenge.lastWorkTimeUpdate}
          className="text-sm text-muted-foreground"
        />
      ) : (
        <span className="text-sm text-muted-foreground">
          {t('common.notApplicable')}
        </span>
      )
    ),
    workState: (challengeInfo) => {
      if (!challengeInfo.challenge.workState) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      const { solution, lastQuery } = challengeInfo.challenge.workState
      return (
        <div className="text-sm text-foreground space-y-1">
          {solution && (
            <div className="truncate max-w-xs" title={solution}>
              <span className="text-xs text-muted-foreground">Solution:</span>
              <div className="truncate">{solution}</div>
            </div>
          )}
          {lastQuery && (
            <div className="truncate max-w-xs" title={lastQuery}>
              <span className="text-xs text-muted-foreground">Last Query:</span>
              <div className="truncate font-mono text-xs">{lastQuery}</div>
            </div>
          )}
          {!solution && !lastQuery && (
            <span className="text-sm text-muted-foreground">
              {t('common.notApplicable')}
            </span>
          )}
        </div>
      )
    },
    feedbackText: (challengeInfo) => {
      if (!challengeInfo.challenge.feedbackText) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      return (
        <div className="text-sm text-foreground max-w-xs">
          <div
            className="truncate"
            title={challengeInfo.challenge.feedbackText}
          >
            {challengeInfo.challenge.feedbackText}
          </div>
        </div>
      )
    },
    userFeedback: (challengeInfo) => {
      const userFeedback = challengeInfo.challenge.userFeedback
      if (!userFeedback) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      const ratingValue = Number(userFeedback.overallRating)
      const roundedRating = Math.round(ratingValue)
      const hasValidRating = Number.isFinite(ratingValue) && roundedRating >= 1 && roundedRating <= 10

      if (!hasValidRating) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      return (
        <div
          className="flex items-center gap-1"
          title={userFeedback.freeText?.trim() || undefined}
          aria-label={`User rating: ${roundedRating} out of 6`}
        >
          {Array.from({ length: roundedRating }, (_, index) => (
            <StarIconSolid key={index} className="h-4 w-4 text-warning" aria-hidden="true" />
          ))}
        </div>
      )
    },
  }

  const renderCellContent = (challengeInfo: ChallengeService.ChallengeInfo, columnKey: keyof ChallengeService.Challenge) => {
    const renderer = cellRenderers[columnKey]
    return renderer ? renderer(challengeInfo) : null
  }

  return (
    <div className="bg-card shadow overflow-hidden rounded-md sm:rounded-md border border-border">
      <div className="px-4 py-5 sm:px-6 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg leading-6 font-medium text-foreground">
            {t('challenges.allChallenges')}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {loadingMode ? t('common.loading') : t('common.XRecords', { count: challenges.length })}
          </p>
        </div>
        {tableSettings && (
          <div className="flex items-center gap-2">
            <FilterSetManager
              tableId={tableSettings.tableId}
              sortColumn={tableSettings.sortColumn}
              sortDirection={tableSettings.sortDirection}
              filters={tableSettings.filters}
              visibleColumns={tableSettings.visibleColumns}
              defaultVisibleColumns={tableSettings.defaultVisibleColumns}
              filterLabels={tableSettings.filterLabels}
              setSort={tableSettings.setSort}
              clearColumnFilter={tableSettings.clearColumnFilter}
              clearAllSettings={tableSettings.clearAllSettings}
              applyFilterSet={tableSettings.applyFilterSet}
              columns={tableSettings.availableColumns}
            />
            <ColumnVisibilityDropdown
              availableColumns={tableSettings.availableColumns}
              isColumnVisible={tableSettings.isColumnVisible}
              toggleColumnVisibility={tableSettings.toggleColumnVisibility}
            />
          </div>
        )}
      </div>

      <div className="overflow-x-auto overflow-y-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-secondary/5">
            <tr>
              {visibleColumns.map((column) => {
                const filterKey = column.filterKey ?? column.key
                const isEnumFilter = column.filterType === 'enum'
                const isTextFilter = column.filterType === 'text'
                const isAsyncSearchFilter = column.filterType === 'async-search'
                const filterValue = filterApi && isTextFilter ? filterApi.getColumnFilter(filterKey) : ''
                const filterArray = filterApi && (isEnumFilter || isAsyncSearchFilter) ? filterApi.getColumnFilterArray(filterKey) : []
                const hasFilter = isTextFilter ? !!filterValue?.trim() : (filterArray?.length ?? 0) > 0
                const enumOptions = isEnumFilter ? getEnumOptionsForColumn(filterKey) : undefined

                return (
                  <th
                    key={column.key}
                    className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider"
                  >
                    <div className="flex items-center gap-2">
                      {column.sortable ? (
                        <button
                          onClick={() => onSort(column.key)}
                          className="group flex items-center gap-1.5 hover:text-foreground transition-colors"
                        >
                          <span>{t(column.label)}</span>
                          {renderSortIcon(
                            sortField === column.key ? sortDirection : null
                          )}
                        </button>
                      ) : (
                        <span>{t(column.label)}</span>
                      )}
                      {isEnumFilter && enumOptions && filterApi && (
                        <EnumFilterDropdown
                          label={column.label}
                          labelT={t}
                          enumOptions={enumOptions}
                          filterArray={filterArray}
                          hasFilter={hasFilter}
                          onToggleFilterItem={(value) =>
                            filterApi.toggleColumnFilterArrayItem(
                              filterKey,
                              value
                            )
                          }
                          onClearFilter={() =>
                            filterApi.clearColumnFilter(filterKey)
                          }
                        />
                      )}
                      {isTextFilter && filterApi && (
                        <TextFilterPopover
                          label={column.label}
                          labelT={t}
                          filterValue={filterValue}
                          hasFilter={hasFilter}
                          onFilterChange={(value) =>
                            filterApi.setColumnFilter(filterKey, value)
                          }
                          onClearFilter={() =>
                            filterApi.clearColumnFilter(filterKey)
                          }
                        />
                      )}
                      {isAsyncSearchFilter &&
                        filterApi &&
                        asyncSearchFetchers?.[filterKey] && (
                          <AsyncMultiPick
                            mode={filterKey === 'taskId' ? 'preload' : 'search'}
                            selected={filterArray}
                            onChange={(values) =>
                              filterApi.setColumnFilterArray(filterKey, values)
                            }
                            fetchOptions={asyncSearchFetchers[filterKey]}
                            labels={{
                              placeholder: t('common.search'),
                              searchPlaceholder: t('common.search'),
                            }}
                            listClassName="w-[360px]"
                            customTrigger={({ selected }) => (
                              <button
                                className={`flex items-center justify-center h-4 w-4 hover:text-foreground transition-colors ${selected.length > 0
                                  ? 'text-accent'
                                  : 'text-muted-foreground/50'
                                  }`}
                              >
                                <FunnelIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                          />
                        )}
                    </div>
                  </th>
                )
              })}
              <th
                className="px-4 py-3 text-end text-xs font-medium text-muted-foreground uppercase tracking-wider sticky right-0 z-20"
              >
                <span className="sr-only">
                  {t('common.actions')}
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {loadingMode ? (
              <tr>
                <td colSpan={visibleColumns.length + 1} className="px-6 py-12 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                    <span className="ms-2 text-sm text-muted-foreground">{t('common.loading')}</span>
                  </div>
                </td>
              </tr>
            ) : hasNoFilterResults ? (
              <tr>
                <td colSpan={visibleColumns.length + 1} className="px-6 py-8">
                  <NoDataEmptyState
                    icon={FunnelIcon}
                    title={t('common.noFilterResults')}
                    description={t('common.noFilterResultsDescription')}
                    buttonText={onClearFilters ? t('common.resetFilters') : undefined}
                    onButtonClick={onClearFilters}
                  />
                </td>
              </tr>
            ) : (
              challenges.map((challengeInfo, index) => {
                const isLoadMoreSentinel = hasMore && !loadingMode && index === loadMoreSentinelIndex

                return (
                  <tr
                    key={challengeInfo.challenge.id}
                    ref={isLoadMoreSentinel ? loadMoreSentinelRef : undefined}
                  >
                    {visibleColumns.map((column) => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {renderCellContent(challengeInfo, column.key)}
                      </td>
                    ))}
                    <td className="px-4 py-4 whitespace-nowrap text-end sticky right-0 z-10 bg-card">
                      <DropdownMenu
                        open={openDropdownChallengeId === challengeInfo.challenge.id}
                        onOpenChange={(open) => {
                          setOpenDropdownChallengeId(open ? challengeInfo.challenge.id : null)
                          if (!open) setResubmitConfirmId(null)
                        }}
                      >
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 p-0"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={t('common.actions')}
                          >
                            <EllipsisVerticalIcon className="h-5 w-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              onChallengeClick?.(challengeInfo)
                            }}
                          >
                            <EyeIcon className="h-4 w-4 me-2" />
                            {t('common.view')}
                          </DropdownMenuItem>
                          {challengeInfo.challenge.feedbackStatus === ChallengeService.FeedbackStatusEnum.Processed && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                onViewFeedback?.(challengeInfo)
                              }}
                            >
                              <ChatBubbleLeftRightIcon className="h-4 w-4 me-2" />
                              {t('challenges.viewFeedback')}
                            </DropdownMenuItem>
                          )}
                          {Boolean(
                            challengeInfo.challenge.employerFeedback &&
                            (challengeInfo.challenge.employerFeedback.feedback ||
                              challengeInfo.challenge.employerFeedback.grade != null)
                          ) && (
                              <>
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onViewEmployerFeedback?.(challengeInfo)
                                  }}
                                >
                                  <BriefcaseIcon className="h-4 w-4 me-2" />
                                  {t('challenges.viewEmployerFeedback')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onSelect={(e) => {
                                    e.preventDefault()
                                    if (pdfDownloadingChallengeId !== challengeInfo.challenge.id) {
                                      void handleDownloadFeedbackPdf(challengeInfo.challenge.id)
                                    }
                                  }}
                                  disabled={pdfDownloadingChallengeId === challengeInfo.challenge.id}
                                  className="flex items-center gap-2"
                                >
                                  {pdfDownloadingChallengeId === challengeInfo.challenge.id ? (
                                    <>
                                      <StandardLoader size="sm" inline className="!m-0" />
                                      {t('common.generating')}
                                    </>
                                  ) : (
                                    <>
                                      <ArrowDownTrayIcon className="h-4 w-4 me-2" />
                                      {t('challenges.downloadFeedbackPdf')}
                                    </>
                                  )}
                                </DropdownMenuItem>
                              </>
                            )}
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              onViewSolution?.(challengeInfo)
                            }}
                          >
                            <PencilSquareIcon className="h-4 w-4 me-2" />
                            {t('challenges.viewSolution')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              void navigator.clipboard.writeText(challengeInfo.challenge.id)
                            }}
                          >
                            <ClipboardDocumentListIcon className="h-4 w-4 me-2" />
                            {t('common.copyId')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              void navigator.clipboard.writeText(challengeInfo.challenge.userId)
                            }}
                          >
                            <ClipboardDocumentListIcon className="h-4 w-4 me-2" />
                            {t('common.copyUserId')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              void navigator.clipboard.writeText(challengeInfo.challenge.taskId)
                            }}
                          >
                            <ClipboardDocumentListIcon className="h-4 w-4 me-2" />
                            {t('common.copyTaskId')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={(event) => {
                              event.preventDefault()
                              const challengeId = challengeInfo.challenge.id
                              const effectiveValue =
                                feedbackReleaseState[challengeId] ??
                                Boolean(challengeInfo.challenge.feedbackReleaseOverride)
                              void handleToggleFeedbackRelease(
                                challengeInfo,
                                !effectiveValue
                              )
                            }}
                          >
                            <ArrowUpOnSquareIcon className="h-4 w-4 me-2" />
                            {(() => {
                              const challengeId = challengeInfo.challenge.id
                              const effectiveValue =
                                feedbackReleaseState[challengeId] ??
                                Boolean(challengeInfo.challenge.feedbackReleaseOverride)
                              return effectiveValue
                                ? t('challenges.unreleaseFeedback')
                                : t('challenges.releaseFeedback')
                            })()}
                          </DropdownMenuItem>
                          {challengeInfo.challenge.status === ChallengeService.ChallengeStatusEnum.Finished && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                void handleResubmitChallenge(e, challengeInfo)
                              }}
                              disabled={!!resubmitPending[challengeInfo.challenge.id]}
                            >
                              <ArrowPathIcon className="h-4 w-4 me-2" />
                              {resubmitConfirmId === challengeInfo.challenge.id ? t('common.areYouSure') : t('challenges.resubmit')}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={(e) => {
                              void handleDeleteMenuClick(e, challengeInfo)
                            }}
                            aria-disabled={
                              deleteConfirmId === challengeInfo.challenge.id && !deleteConfirmReady
                            }
                            className={`text-destructive focus:text-destructive ${deleteConfirmId === challengeInfo.challenge.id && !deleteConfirmReady
                              ? 'opacity-70 pointer-events-none'
                              : ''
                              }`}
                          >
                            <TrashIcon className="h-4 w-4 me-2" />
                            {deleteConfirmId === challengeInfo.challenge.id ? t('common.areYouSure') : t('common.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {hasMore && !loadingMode && (
        <div className="flex items-center justify-center py-4">
          {isPageLoading && (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent"></div>
              <span className="ms-2 text-sm text-muted-foreground">{t('common.loading')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
